// Subida de la foto propia de los modos que parten de una imagen del usuario:
// "Animar imagen" (foto → video) y "Carrusel" (foto → variaciones).
//
// A propósito NO escribe en `imagenes_generadas`: esa tabla es el registro de lo
// que GENERÓ Atlas, y meterle fotos del usuario ensuciaría historial, analytics y
// conteos. El archivo queda solo en Storage y el backend lo lee por REFERENCIA
// (bucket + path), no por URL firmada — una firma puede vencer durante un job de
// video de varios minutos. Ver ADR-2026-08-14-modo-animar-imagen y
// ADR-2026-08-14-modo-carrusel.

import { supabase } from './supabase';
import {
  UnsupportedImageFormatError,
  normalizeImageForUpload,
} from '../utils/heicConvert';

/** Mismo bucket que usa Shorts Studio para el material de origen de un video. */
export const ANIMAR_BUCKET = 'video-project-assets';

/** Carpeta dentro del bucket — una por modo, para saber de dónde salió cada archivo.
 *  El backend valida el prefijo `{company}/`, no la carpeta, así que sumar una no
 *  necesita cambios del otro lado. */
export type CarpetaSubida = 'animar' | 'carrusel';

/** El usuario puede quedarse un rato en la pantalla de configurar antes de generar. */
const PREVIEW_TTL_SECONDS = 60 * 60 * 3;

export interface ImagenParaAnimar {
  bucket: string;
  /** Path dentro del bucket — es lo que viaja al backend. */
  path: string;
  /** URL firmada, solo para mostrarla en pantalla. */
  previewUrl: string;
}

/**
 * Normaliza (HEIC/HEIF/AVIF → JPEG, rechaza RAW/TIFF) y sube la foto del usuario.
 * Lanza `Error` con mensaje mostrable: los formatos bloqueados salen con el copy
 * compartido de `UnsupportedImageFormatError`.
 */
export async function subirImagenParaAnimar(
  file: File,
  companyId: string,
  carpeta: CarpetaSubida = 'animar',
): Promise<ImagenParaAnimar> {
  // `normalizeImageForUpload` es la política ÚNICA de formatos de la app: rechaza
  // los bloqueados y convierte lo que el pipeline de video no sabe leer.
  let normalizado: File;
  try {
    normalizado = await normalizeImageForUpload(file);
  } catch (err) {
    if (err instanceof UnsupportedImageFormatError) throw new Error(err.message);
    throw new Error('No pudimos procesar esa imagen. Probá con otra.');
  }

  const ext = (normalizado.name.split('.').pop() || 'jpg').toLowerCase();
  const path = `${companyId}/${carpeta}/${crypto.randomUUID()}.${ext}`;

  const { error: upErr } = await supabase.storage
    .from(ANIMAR_BUCKET)
    .upload(path, normalizado, {
      contentType: normalizado.type || 'image/jpeg',
      upsert: false,
    });
  if (upErr) throw new Error('No se pudo subir la imagen. Probá de nuevo.');

  const { data: signed } = await supabase.storage
    .from(ANIMAR_BUCKET)
    .createSignedUrl(path, PREVIEW_TTL_SECONDS);
  if (!signed?.signedUrl) throw new Error('No se pudo preparar la imagen subida.');

  return { bucket: ANIMAR_BUCKET, path, previewUrl: signed.signedUrl };
}
